/**
 * NGO Referral Landing — /join/:slug
 *
 * Public page a person lands on after scanning a Handout Card QR. Fetches the
 * partner display info, then either:
 *   1. Shows a warm welcome + a "Claim my free year" signup form for guests
 *   2. Redirects logged-in members to /dashboard (they already have access)
 *
 * On success we set the JWT cookie server-side, refresh the auth context, and
 * route the user to /welcome (existing onboarding). Charter-stacking messaging
 * is surfaced prominently so people understand the free year is additive.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CharterCounter } from '../components/CharterCounter';
import {
  Gift,
  Heart,
  Loader2,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Globe,
  MapPin,
  Phone,
  Mail,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinNgoPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    email: '',
    username: '',
    display_name: '',
    password: '',
  });

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/ngos/${slug}`);
        if (!cancel) setPartner(res.data);
      } catch (e) {
        if (!cancel) setNotFound(true);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slug]);

  // Members already have access — send them to the dashboard.
  useEffect(() => {
    if (user && partner) {
      toast.info(`You're already a member. Enjoy the club!`);
      navigate('/dashboard');
    }
  }, [user, partner, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/^[a-zA-Z0-9_-]{3,30}$/.test(form.username)) {
      toast.error('Username: 3–30 chars, letters/numbers/underscore/dash only');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/ngos/${slug}/redeem`, {
        email: form.email.trim(),
        username: form.username.trim(),
        display_name: form.display_name.trim(),
        password: form.password,
      });
      const { token } = res.data;
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      await refreshUser();
      toast.success(
        `Welcome! ${partner?.name || 'Your NGO'} gifted you a full year — check your email to verify.`
      );
      navigate('/welcome?from=ngo');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Sorry, we could not create your account. Please try again.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="join-ngo-loading">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  if (notFound || !partner) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center" data-testid="join-ngo-not-found">
        <SEO title="Partner not found — the Club" description="This NGO partner link isn't active." />
        <h1 className="text-3xl font-bold mb-3">This link isn't active</h1>
        <p className="text-muted-foreground mb-6">
          The partner code <code className="rounded bg-muted px-1.5 py-0.5">{slug}</code> isn't in our system,
          or it's been deactivated. Double-check the QR code, or head to the main site.
        </p>
        <Button asChild data-testid="join-ngo-home-cta">
          <Link to="/">Go to the Club</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white" data-testid="join-ngo-page">
      <SEO
        title={`${partner.name} + the Club — 1 year free`}
        description={`${partner.name} gave you this card. Claim your free year with the American Dog Club.`}
      />
      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 mb-4">
            <Gift className="h-3.5 w-3.5" />
            Free year courtesy of {partner.name}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-3">
            Welcome, new dog parent
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            {partner.name} adopted this out to your family, and gifted you a{' '}
            <span className="font-semibold text-amber-700">12-month full membership</span> to
            the American Dog Club. No card required.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-6">
          {/* Signup form */}
          <Card className="md:col-span-3 border-amber-200">
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Claim your free year</h2>
              <p className="text-sm text-muted-foreground mb-5">
                Takes 30 seconds. We'll email you a verification link to activate.
              </p>
              <form onSubmit={submit} className="space-y-4" data-testid="join-ngo-form">
                <div>
                  <Label htmlFor="email" className="text-xs">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={form.email}
                    onChange={upd('email')}
                    data-testid="join-ngo-email"
                    placeholder="you@example.com"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="username" className="text-xs">Username</Label>
                    <Input
                      id="username"
                      required
                      minLength={3}
                      maxLength={30}
                      value={form.username}
                      onChange={upd('username')}
                      data-testid="join-ngo-username"
                      placeholder="rex_owner"
                    />
                  </div>
                  <div>
                    <Label htmlFor="display_name" className="text-xs">Display name</Label>
                    <Input
                      id="display_name"
                      required
                      value={form.display_name}
                      onChange={upd('display_name')}
                      data-testid="join-ngo-displayname"
                      placeholder="Rex's Human"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs">Password (8+ chars)</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={form.password}
                    onChange={upd('password')}
                    data-testid="join-ngo-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-amber-500 hover:bg-amber-600"
                  data-testid="join-ngo-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account…
                    </>
                  ) : (
                    <>
                      Claim my free year
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  By joining you agree to our{' '}
                  <Link to="/about/service-agreement" className="underline">Service Agreement</Link> and{' '}
                  <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Right rail: partner + perks + stacking pitch */}
          <div className="md:col-span-2 space-y-4">
            <Card className="border-amber-200 overflow-hidden">
              <CardContent className="p-5">
                {partner.logo_url ? (
                  <img
                    src={partner.logo_url}
                    alt={partner.name}
                    className="h-16 w-auto mb-3 rounded"
                  />
                ) : (
                  <div className="h-16 w-16 mb-3 rounded-full bg-amber-100 flex items-center justify-center">
                    <Heart className="h-8 w-8 text-amber-600" />
                  </div>
                )}
                <p className="font-bold text-slate-900">{partner.name}</p>
                {partner.tagline && (
                  <p className="text-sm text-muted-foreground mt-1">{partner.tagline}</p>
                )}
                <div className="mt-4 space-y-1.5 text-xs text-slate-600">
                  {partner.address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
                      <span>{partner.address}</span>
                    </div>
                  )}
                  {partner.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                      <span>{partner.phone}</span>
                    </div>
                  )}
                  {partner.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                      <a href={`mailto:${partner.email}`} className="hover:underline">
                        {partner.email}
                      </a>
                    </div>
                  )}
                  {partner.website && (
                    <div className="flex items-center gap-2">
                      <Globe className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                      <a
                        href={partner.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline break-all"
                      >
                        {partner.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-emerald-200 bg-emerald-50/40">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm mb-2">
                  <CheckCircle2 className="h-4 w-4" />
                  What's included
                </div>
                <ul className="text-sm text-slate-700 space-y-1.5">
                  <li>• Full club membership for 12 months</li>
                  <li>• Voting rights on club decisions</li>
                  <li>• Marketplace, matchmaking, unlimited posts</li>
                  <li>• Ad-free feed</li>
                  <li>• Founding-cohort badge on your profile</li>
                </ul>
              </CardContent>
            </Card>

            {/* Charter-stacking pitch (per ROADMAP §Stacking) */}
            <Card className="border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm mb-2">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  Want to lock in Charter pricing on top?
                </div>
                <p className="text-sm text-slate-700 mb-3">
                  You've got a free year from {partner.name}. If you also buy Charter now,{' '}
                  <span className="font-semibold">your paid membership starts when your free year ends</span> —
                  nothing lost. Charter time stacks on top.
                </p>
                <CharterCounter variant="compact" />
              </CardContent>
            </Card>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          Already a member?{' '}
          <Link to="/auth/login" className="underline" data-testid="join-ngo-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
